import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Result,
  Space,
  Steps,
  theme,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import Loader from "../controls/Loader";
import {
  resetPassword,
  resetPasswordCode,
} from "../repository/auth.repository";

export interface ResetPasswordWizardProps {
  readonly open?: boolean;
  readonly onCancel?: () => void;
}

function ResetPasswordWizard(props: ResetPasswordWizardProps) {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const values = Form.useWatch<any>([], form);

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [finish, setFinish] = useState(false);
  const [error, setError] = useState(false);
  const [valuesValid, setValuesValid] = useState(false);
  const [valuesChange, setValuesChange] = useState<any>({});
  const [email, setEmail] = useState<string>("");
  const [code, setCode] = useState<string>("");

  useEffect(() => {
    reset();
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then(() => {
        setValuesValid(true);
      })
      .catch(() => {
        setValuesValid(false);
      });
    setValuesChange(values);
  }, [form, values]);

  function onFormChange(): void {
    setError(false);
  }

  function onNext(): void {
    setLoading(true);
    setError(false);
    if (step === 0) {
      resetPasswordCode({
        email: valuesChange.email,
      })
        .then((code) => {
          setEmail(valuesChange.email);
          setCode(code);
          setLoading(false);
          setStep(1);
        })
        .catch(() => {
          setLoading(false);
          setError(true);
        });
    } else if (step === 1) {
      resetPassword({
        code,
        oldPassword: valuesChange.oldPassword,
        newPassword: valuesChange.newPassword,
      })
        .then(() => {
          setLoading(false);
          setFinish(true);
          setStep(2);
        })
        .catch(() => {
          setLoading(false);
          setError(true);
        });
    }
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  // Methods

  function reset(): void {
    setStep(0);
    setValuesValid(false);
    setValuesChange({});
    setFinish(false);
    setError(false);
    form.resetFields();
  }

  const steps = [
    {
      title: "Email",
    },
    {
      title: "Reset",
    },
    {
      title: "Complete",
    },
  ];

  return (
    <Modal
      open={props.open}
      centered
      title={"Reset Password"}
      footer={null}
      maskClosable={false}
      onCancel={onCancel}
    >
      <Col className="flex min-w-[392px] max-w-[512px] flex-col overflow-hidden">
        {/* Steps */}
        <Steps current={step} items={steps} />
        {/* Content */}
        <Col className="flex max-h-[392px] min-h-[392px] flex-col overflow-hidden overflow-y-auto">
          {!loading && step === 0 && (
            <Col className="flex flex-auto flex-col items-center justify-center pb-4 pt-4">
              <Typography.Paragraph className="text-center">
                Enter the email address of the account that needs to be reset.
              </Typography.Paragraph>
              <Form
                className="my-form-item flex w-96 flex-col items-center"
                form={form}
                layout={"horizontal"}
                variant={"filled"}
                colon={false}
                autoComplete="off"
                initialValues={{}}
                onChange={onFormChange}
              >
                <Form.Item
                  className="mb-2"
                  name={"email"}
                  label={
                    <Typography.Text
                      style={{
                        color: token.colorTextDescription,
                      }}
                    >
                      Email
                    </Typography.Text>
                  }
                  rules={[
                    {
                      required: true,
                      type: "email",
                      message: "Invalid email format",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Form>
              {error && (
                <Typography.Text className="mt-4 text-center" type="danger">
                  Failed to receive code
                </Typography.Text>
              )}
            </Col>
          )}
          {!loading && step === 1 && (
            <Col className="flex flex-auto flex-col items-center justify-center pb-4 pt-4">
              <Typography.Paragraph className="text-center">
                Enter the old password and enter the new password for{" "}
                {
                  <Typography.Text className="font-bold">
                    {email}
                  </Typography.Text>
                }
                .
              </Typography.Paragraph>
              <Form
                className="my-form-item flex w-72 flex-col"
                form={form}
                layout={"vertical"}
                variant={"filled"}
                colon={false}
                autoComplete="off"
                initialValues={{}}
                onChange={onFormChange}
              >
                <Form.Item
                  className="mb-2"
                  name={"oldPassword"}
                  label={
                    <Typography.Text
                      className="text-sm"
                      style={{
                        color: token.colorTextDescription,
                      }}
                    >
                      Old Password
                    </Typography.Text>
                  }
                  rules={[
                    {
                      required: true,
                      message: "Old password is required",
                    },
                  ]}
                >
                  <Input.Password />
                </Form.Item>
                <Form.Item
                  className="mb-2"
                  name={"newPassword"}
                  label={
                    <Typography.Text
                      className="text-sm"
                      style={{
                        color: token.colorTextDescription,
                      }}
                    >
                      New Password
                    </Typography.Text>
                  }
                  rules={[
                    {
                      required: true,
                      message: "New password is required",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("oldPassword") !== value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Old password cannot be reused"),
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password />
                </Form.Item>
                <Form.Item
                  className="mb-2"
                  name={"confirmPassword"}
                  label={
                    <Typography.Text
                      className="text-sm"
                      style={{
                        color: token.colorTextDescription,
                      }}
                    >
                      Confirm Password
                    </Typography.Text>
                  }
                  dependencies={["newPassword"]}
                  rules={[
                    {
                      required: true,
                      message: "Confirm password is required",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("newPassword") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("New password mismatch"),
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password />
                </Form.Item>
              </Form>
              {error && (
                <Typography.Text className="mt-4 text-center" type="danger">
                  Something went wrong, check fields
                </Typography.Text>
              )}
            </Col>
          )}
          {!loading && step === 2 && (
            <Col className="flex flex-auto flex-col items-center justify-center pb-4 pt-4">
              <Result
                status={finish ? "success" : "error"}
                title={
                  finish
                    ? "Password changed successfully"
                    : "Something went wrong, try again"
                }
              />
            </Col>
          )}
          {loading && (
            <Col className="flex flex-auto flex-col items-center justify-center pb-4 pt-4">
              {step === 0 && (
                <Typography.Paragraph>
                  The code will be sent to your email...
                </Typography.Paragraph>
              )}
              {step === 1 && (
                <Typography.Paragraph>
                  Wait for code confirmation...
                </Typography.Paragraph>
              )}
              {step === 2 && (
                <Typography.Paragraph>
                  Password is being updated...
                </Typography.Paragraph>
              )}
              <Loader />
            </Col>
          )}
        </Col>
        {step < 2 && (
          <Space className="sticky bottom-0 flex flex-row items-center justify-end">
            <Button className="ml-2" onClick={onCancel}>
              Close
            </Button>
            <Button type="primary" disabled={!valuesValid} onClick={onNext}>
              {(() => {
                if (step === 0) {
                  return "Send";
                } else if (step === 1) {
                  return "Change";
                } else {
                  return "Done";
                }
              })()}
            </Button>
          </Space>
        )}
      </Col>
    </Modal>
  );
}

export default ResetPasswordWizard;
